import type { ReactNode } from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@reflex/ui/components/ui/hover-card';
import { RESOURCE_PREVIEW_HOVER_CONTENT_CLASS } from '@reflex/ui/components/resource-preview/ResourcePreviewCard';
import { ResourcePreview } from '@/components/resource-preview/ResourcePreview';

interface AgentHoverPreviewProps {
  /** Agent id to fetch and preview on hover. */
  agentId: string;
  /** The status/activity badge that acts as the hover trigger. */
  children: ReactNode;
}

/**
 * Wraps an agent status/activity badge in a hover popover that previews the
 * agent (name, status, type, model, prompt, owner). Mirrors the
 * {@link import('@/components/status/SandboxStatusBadge').SandboxStatusBadge}
 * hover pattern so status indicators reveal a richer preview consistently
 * across the agents list and run views.
 */
export function AgentHoverPreview({ agentId, children }: AgentHoverPreviewProps) {
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label="Preview agent"
          data-testid={`agent-status-preview-${agentId}`}
          className="inline-flex w-fit shrink-0 cursor-default rounded-md align-middle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {children}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className={RESOURCE_PREVIEW_HOVER_CONTENT_CLASS}
        aria-label="Agent preview"
      >
        <ResourcePreview kind="agents" id={agentId} />
      </HoverCardContent>
    </HoverCard>
  );
}
